import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { isStandalone } from "@/lib/settings";

/**
 * Persistent "Get the App" header button.
 * - Hidden entirely if the app is already running in standalone (installed) mode.
 * - Captures `beforeinstallprompt` so Chromium browsers can trigger native install.
 * - Falls back to a helpful modal for iOS Safari (no event support) or
 *   browsers that don't expose the prompt at all (Firefox mobile, Brave, etc.).
 *
 * This is independent of the floating `InstallPwaPrompt` card — that one is a
 * one-time nudge, while this button is the always-available access point.
 */
export default function InstallAppButton({ compact = false }) {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch { /* ignore */ }
      setDeferred(null);
    } else {
      // No native event available — show a per-browser help modal
      setShowHelp(true);
    }
  };

  const ua = typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
  const isIos = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !/edg/.test(ua);
  const isFirefox = /firefox|fxios/.test(ua);
  const isSafari = /safari/.test(ua) && !/crios|fxios|chrome|android/.test(ua);

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={handleClick}
        title={t("install.cta") || "Install App"}
        className={
          compact
            ? "inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border border-[#D4AF37]/60 text-[#D4AF37] px-2.5 py-1.5 rounded-sm hover:bg-[#D4AF37] hover:text-[#0F162A] transition-colors"
            : "inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-[#D4AF37] text-[#D4AF37] px-3 py-2 rounded-sm hover:bg-[#D4AF37] hover:text-[#0F162A] transition-colors"
        }
      >
        <Download className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{t("install.cta") || "Install App"}</span>
        <span className="sm:hidden">App</span>
      </button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="bg-[#0F162A] border border-[#D4AF37]/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#D4AF37] flex items-center gap-2">
              <Download className="w-5 h-5" /> {t("install.title")}
            </DialogTitle>
            <DialogDescription className="text-white/75 leading-relaxed pt-2">
              {t("install.body")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm text-white/85 leading-relaxed">
            {isIos && isSafari && (
              <div className="bg-[#0A1128] border border-white/10 rounded-sm p-4">
                <div className="eyebrow text-[#D4AF37] mb-2">iPhone / iPad · Safari</div>
                <ol className="list-decimal list-inside space-y-1.5">
                  <li>{t("install.body_ios_a")} <span className="inline-block px-1.5 py-0.5 border border-white/30 rounded text-[10px]">{t("install.body_ios_b")}</span></li>
                  <li>{t("install.body_ios_c")} <strong className="text-[#D4AF37]">{t("install.body_ios_d")}</strong></li>
                  <li>{t("install.body_ios_e")}</li>
                </ol>
              </div>
            )}

            {isAndroid && isChrome && (
              <div className="bg-[#0A1128] border border-white/10 rounded-sm p-4">
                <div className="eyebrow text-[#D4AF37] mb-2">Android · Chrome</div>
                <ol className="list-decimal list-inside space-y-1.5">
                  <li>Tap the <strong className="text-[#D4AF37]">⋮ menu</strong> in the top-right of Chrome.</li>
                  <li>Choose <strong className="text-[#D4AF37]">Install app</strong> or <strong className="text-[#D4AF37]">Add to Home Screen</strong>.</li>
                  <li>Confirm. The app icon will appear on your home screen.</li>
                </ol>
              </div>
            )}

            {isFirefox && (
              <div className="bg-[#0A1128] border border-white/10 rounded-sm p-4">
                <div className="eyebrow text-[#D4AF37] mb-2">Firefox</div>
                <p>Firefox doesn{`'`}t support direct install on this device. For the full app experience, please open ADN Rewards in <strong className="text-[#D4AF37]">Chrome</strong>, <strong className="text-[#D4AF37]">Edge</strong>, or <strong className="text-[#D4AF37]">Safari</strong> and tap {`"`}Install{`"`} or {`"`}Add to Home Screen{`"`}.</p>
              </div>
            )}

            {!isIos && !isAndroid && (
              <div className="bg-[#0A1128] border border-white/10 rounded-sm p-4">
                <div className="eyebrow text-[#D4AF37] mb-2">Desktop</div>
                <ol className="list-decimal list-inside space-y-1.5">
                  <li>Look for the <strong className="text-[#D4AF37]">install icon</strong> in your browser{`'`}s address bar (looks like a small monitor with a down arrow).</li>
                  <li>Or open your browser menu and choose <strong className="text-[#D4AF37]">{`"`}Install ADN Rewards{`"`}</strong> / <strong className="text-[#D4AF37]">{`"`}Apps → Install this site as an app{`"`}</strong>.</li>
                  <li>Confirm. ADN will run in its own window.</li>
                </ol>
              </div>
            )}

            {!isIos && !isAndroid && !isFirefox && (
              <div className="text-xs text-white/55">
                Still don{`'`}t see the option? Your browser may not support PWA installation. Try Chrome, Edge, or Safari.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
